<script setup lang="ts">
    /**
     * A light that runs along the border of the box it sits in while the
     * agent works (spec `widget-copilot.md`, D5). The parent gives it a
     * positioned box: the beam fills it and inherits its rounded corners.
     * Decoration only, hidden from assistive technology: the chat already
     * says what the agent is doing.
     */
    defineProps<{
        /** Shown while true, fading in and out. */
        active: boolean
    }>()
</script>

<template>
    <Transition name="pk-working-beam">
        <span v-if="active" class="pk-working-beam" aria-hidden="true">
            <span class="pk-working-beam__halo" />
            <span class="pk-working-beam__ring">
                <span class="pk-working-beam__light" />
            </span>
        </span>
    </Transition>
</template>

<style lang="scss">
    // Only a transform and an opacity move: the light turns and the halo
    // fades on the graphics card, and no frame repaints the page. A
    // registered `@property` angle on the gradient would be shorter to write
    // and would repaint the gradient on every frame.
    // `--pk-working-beam-color` sets the color from outside; without it the
    // beam takes the agent's color in the widget, the brand elsewhere.
    .pk-working-beam {
        --pk-working-beam-source: var(
            --pk-working-beam-color,
            var(--chatbot-main-color, var(--color-brand))
        );
        --pk-working-beam-main: var(--pk-working-beam-source);
        --pk-working-beam-tone-before: var(--pk-working-beam-main);
        --pk-working-beam-tone-after: var(--pk-working-beam-main);

        position: absolute;
        inset: 0;
        border-radius: inherit;
        pointer-events: none;

        // The light below reads the beam's size in `cqw` and `cqh`
        container-type: size;

        // Where the browser can derive them: the main color held to a middle
        // lightness, so a near-black or a pale brand still reads as a light on
        // either theme, and two neighbours of it on the hue wheel. The main
        // color alone otherwise.
        @supports (color: oklch(from red l c h)) {
            --pk-working-beam-main: oklch(
                from var(--pk-working-beam-source) clamp(0.55, l, 0.75) c h
            );
            --pk-working-beam-tone-before: oklch(
                from var(--pk-working-beam-main) l c calc(h - 40)
            );
            --pk-working-beam-tone-after: oklch(
                from var(--pk-working-beam-main) l c calc(h + 40)
            );
        }

        // Inside the edge, and around it where the box does not clip: every
        // box shows the inner glow, one that clips its content simply cuts
        // the outer one.
        &__halo {
            position: absolute;
            inset: 0;
            border-radius: inherit;
            box-shadow:
                inset 0 0 var(--spacing-32)
                    color-mix(
                        in srgb,
                        var(--pk-working-beam-main) 60%,
                        transparent
                    ),
                0 0 var(--spacing-24)
                    color-mix(
                        in srgb,
                        var(--pk-working-beam-main) 45%,
                        transparent
                    );
            animation: pk-working-beam-pulse 2s ease-in-out infinite alternate;
        }

        // Shows only a border of the light: the content box is cut out
        &__ring {
            position: absolute;
            inset: 0;
            border-radius: inherit;
            padding: var(--spacing-3);
            overflow: hidden;
            mask:
                linear-gradient(#000 0 0) content-box,
                linear-gradient(#000 0 0);
            mask-composite: exclude;
            -webkit-mask-composite: xor;
        }

        // A square as wide as the box's diagonal, so the corners stay
        // covered whatever the angle. The sum of the sides where `hypot()`
        // is missing: larger, same picture. A fallback written as a second
        // `width` does not survive the build, whose CSS minifier keeps only
        // the last declaration.
        &__light {
            position: absolute;
            top: 50%;
            left: 50%;
            width: calc(100cqw + 100cqh);
            aspect-ratio: 1;

            // Two lights on opposite sides, each a third of the turn long:
            // one alone left most of a large frame dark at any moment
            background: conic-gradient(
                transparent 0 12%,
                var(--pk-working-beam-tone-before) 24%,
                var(--pk-working-beam-main) 38%,
                var(--pk-working-beam-tone-after) 46%,
                transparent 50% 62%,
                var(--pk-working-beam-tone-before) 74%,
                var(--pk-working-beam-main) 88%,
                var(--pk-working-beam-tone-after) 96%,
                transparent
            );
            transform: translate(-50%, -50%);
            animation: pk-working-beam-turn 4s linear infinite;

            @supports (width: hypot(1px, 1px)) {
                width: hypot(100cqw, 100cqh);
            }
        }

        // The signal stays, the motion goes: the whole border lit and still
        @media (prefers-reduced-motion: reduce) {
            &__halo {
                animation: none;
            }

            &__light {
                animation: none;
                background: var(--pk-working-beam-main);
            }
        }
    }

    .pk-working-beam-enter-active,
    .pk-working-beam-leave-active {
        transition: opacity var(--duration-300) ease-out;
    }

    .pk-working-beam-enter-from,
    .pk-working-beam-leave-to {
        opacity: 0;
    }

    @keyframes pk-working-beam-turn {
        to {
            transform: translate(-50%, -50%) rotate(1turn);
        }
    }

    @keyframes pk-working-beam-pulse {
        from {
            opacity: 0.55;
        }
    }
</style>
